/**
 * About Us Page
 *
 * GiftSangam brand story, philosophy, mission/vision, founder's message, and differentiators
 */

import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  Button,
  Divider,
} from '@shopify/polaris';


const whyChooseUs = [
  {
    number: '01',
    title: 'Sourcing Expertise, Not Just Reselling',
    description:
      'Unlike standard vendors who rely on limited stock, we leverage deep Global Sourcing Networks. If you can imagine it, we can source it. From high-tech gadgets to handcrafted artisanal pieces, we find the right product that matches your budget and your intent.',
  },
  {
    number: '02',
    title: 'The "Quality First" Mandate',
    description:
      'We believe that a corporate gift is a direct reflection of your company\'s standards. Every item that leaves our warehouse undergoes a rigorous Quality Check. We ensure that the durability of the gift matches the durability of the relationship you are trying to build.',
  },
  {
    number: '03',
    title: 'Bespoke Branding & Customization',
    description:
      'Your logo deserves more than a sticker. We specialize in High-Quality Printing, Embossing, and Engraving technologies that integrate your brand seamlessly into the product design. We create merchandise that your clients and employees will be proud to use and wear.',
  },
  {
    number: '04',
    title: 'Pan-India Logistics & Timely Delivery',
    description:
      'We understand that in the corporate world, timing is everything. Whether it\'s a Diwali rush or a critical product launch event, our logistics network ensures On-Time Delivery across India, so you never miss a moment to make an impact.',
  },
  {
    number: '05',
    title: 'A Partner for Every Budget',
    description:
      'We believe quality shouldn\'t be held hostage by price. Our diverse catalog offers Premium Solutions for Every Tier\u2014from cost-effective onboarding kits for mass recruitment to luxury hampers for your C-Suite executives.',
  },
];

export function AboutPage() {
  const navigate = useNavigate();

  return (
    <Page title="About Us">
      <Layout>
        {/* Hero Section */}
        <Layout.Section>
          <div className="hero-gradient" style={{ borderRadius: '12px', padding: '48px 32px', textAlign: 'center' }}>
            <BlockStack gap="500" align="center">
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <img
                  src="/tagline.png"
                  alt="Samjhauta nahin, Sangam Hai"
                  style={{
                    maxWidth: '500px',
                    width: '80%',
                    height: 'auto',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </div>

              <Divider />

              <Text as="p" variant="bodyLg" alignment="center">
                <span style={{ color: 'rgba(255,255,255,0.95)' }}>
                  Welcome to <strong>GiftSangam.com</strong>
                </span>
              </Text>
              <Text as="p" variant="bodyMd" alignment="center">
                <span style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '800px', display: 'inline-block' }}>
                  In the dynamic world of business, a gift is never just an object; it is a bridge.
                  It is the unspoken language of appreciation, the tangible proof of a partnership,
                  and the spark that turns a professional acquaintance into a lifelong ally.
                </span>
              </Text>
              <Text as="p" variant="bodyMd" alignment="center">
                <span style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '800px', display: 'inline-block' }}>
                  At <strong>GiftSangam</strong>, we derive our ethos from the Sanskrit word <em>"Sangam"</em>&mdash;meaning
                  union or confluence. We view ourselves as the strategic meeting point where Corporate Gifting
                  Solutions align perfectly with your brand's voice.
                </span>
              </Text>
            </BlockStack>
          </div>
        </Layout.Section>

        {/* Our Philosophy */}
        <Layout.Section>
          <Card>
            <Box padding="600">
              <BlockStack gap="400">
                <Text as="h2" variant="headingLg">
                  Our Philosophy: The Art of the Strategic Alliance
                </Text>
                <Text as="p" variant="bodyMd">
                  We believe that the corporate gifting industry has moved beyond mere catalog selection.
                  It is now about <strong>Relationship Management</strong> and <strong>Brand Storytelling</strong>.
                </Text>
                <Text as="p" variant="bodyMd">
                  We don't just act as vendors; we act as your <strong>Sourcing Partners</strong>.
                  Leveraging deep roots in global sourcing and procurement, we navigate the complex market
                  to find merchandise that isn't just "available," but "exceptional." We understand that
                  your logo on a product is a stamp of your reputation, and we ensure that stamp rests
                  only on items of impeccable integrity.
                </Text>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Mission & Vision */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <Card>
              <Box padding="600">
                <BlockStack gap="300">
                  <Text as="h2" variant="headingLg">
                    Our Mission
                  </Text>
                  <div style={{ borderLeft: '3px solid var(--theme-accent-500)', paddingLeft: '16px' }}>
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      <em>
                        "To revolutionize the corporate gifting landscape by bridging the gap between
                        intent and impact. We are on a mission to curate high-quality, memorable gifting
                        solutions that foster genuine alliances, celebrate milestones, and turn every
                        professional exchange into a personal connection."
                      </em>
                    </Text>
                  </div>
                </BlockStack>
              </Box>
            </Card>

            <Card>
              <Box padding="600">
                <BlockStack gap="300">
                  <Text as="h2" variant="headingLg">
                    Our Vision
                  </Text>
                  <div style={{ borderLeft: '3px solid var(--theme-accent-500)', paddingLeft: '16px' }}>
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      <em>
                        "To be India's most trusted confluence for corporate generosity&mdash;where businesses
                        of all sizes discover not just products, but powerful tools for relationship building.
                        We envision a future where every gift sent through GiftSangam becomes a benchmark
                        for quality, creativity, and strategic thoughtfulness."
                      </em>
                    </Text>
                  </div>
                </BlockStack>
              </Box>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Founder's Message */}
        <Layout.Section>
          <Card>
            <Box padding="600">
              <BlockStack gap="500">
                <Text as="h2" variant="headingLg">
                  From the Desk of Our Founder
                </Text>

                <InlineGrid columns={{ xs: 1, md: '1fr 3fr' }} gap="600">
                  {/* Founder avatar / photo placeholder */}
                  <Box>
                    <div style={{
                      width: '100%',
                      maxWidth: '200px',
                      aspectRatio: '1',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgb(0, 17, 43), var(--theme-accent-500))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}>
                      <span style={{ fontSize: '64px', color: '#ffffff' }}>TG</span>
                    </div>
                  </Box>

                  {/* Founder's letter */}
                  <BlockStack gap="300">
                    <Text as="p" variant="bodyMd">
                      <em>Hello and Welcome to GiftSangam,</em>
                    </Text>
                    <Text as="p" variant="bodyMd">
                      In my journey through the rigorous world of government sourcing and defense logistics,
                      I learned one undeniable truth: <strong>Quality is not an accident; it is a habit.</strong>
                    </Text>
                    <Text as="p" variant="bodyMd">
                      When I founded GiftSangam, I wanted to bring that same discipline and eye for detail
                      into the corporate gifting space. Too often, I saw companies settling for generic,
                      "off-the-shelf" items that ended up in a drawer, forgotten. I realized that a gift
                      is a wasted opportunity if it doesn't carry the weight of your brand's integrity.
                    </Text>
                    <Text as="p" variant="bodyMd">
                      For me, the name <em>'Sangam'</em> is deeply personal. It represents the union of two
                      entities coming together to create something stronger. Whether I am sourcing specialized
                      apparel or curating an executive hamper, my philosophy remains the same: <strong>The right
                      product, for the right person, at the right moment.</strong>
                    </Text>
                    <Text as="p" variant="bodyMd">
                      At GiftSangam, we don't just sell merchandise; we engineer experiences. We understand
                      that when you put your logo on a product, you are putting your reputation in our hands.
                      That is a responsibility I take seriously.
                    </Text>
                    <Text as="p" variant="bodyMd">
                      My team and I are dedicated to being your strategic partners&mdash;helping you navigate
                      the noise to find gifts that speak volumes about your commitment to your employees,
                      your clients, and your partners.
                    </Text>
                    <Text as="p" variant="bodyMd">
                      Thank you for choosing us to be part of your story.
                    </Text>

                    <Divider />

                    <BlockStack gap="100">
                      <Text as="p" variant="bodyMd" fontWeight="bold">
                        Warm Regards,
                      </Text>
                      <Text as="p" variant="headingSm" fontWeight="bold">
                        Tirthankar Gupta
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Founder, GiftSangam (A Unit of Novadhara Ventures)
                      </Text>
                    </BlockStack>
                  </BlockStack>
                </InlineGrid>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Why Choose GiftSangam */}
        <Layout.Section>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h2" variant="headingLg">
                Why Choose GiftSangam?
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Transforming Corporate Gifting from a Transaction to a Trust-Building Exercise.
              </Text>
            </BlockStack>

            <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="400">
              {whyChooseUs.map((item) => (
                <Card key={item.number}>
                  <Box padding="500">
                    <BlockStack gap="300">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'var(--theme-accent-500)',
                        color: '#1a1a1a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '16px',
                      }}>
                        {item.number}
                      </div>
                      <Text as="h3" variant="headingSm" fontWeight="bold">
                        {item.title}
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {item.description}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>
              ))}
            </InlineGrid>
          </BlockStack>
        </Layout.Section>

        {/* Our Promise / CTA */}
        <Layout.Section>
          <Card>
            <div className="hero-gradient" style={{ borderRadius: '12px', padding: '48px 32px', textAlign: 'center' }}>
              <BlockStack gap="400" align="center">
                <Text as="h2" variant="headingXl" alignment="center">
                  <span style={{ color: '#ffffff' }}>Celebrate Partnerships. Elevate Brands.</span>
                </Text>
                <Text as="p" variant="bodyLg" alignment="center">
                  <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                    At GiftSangam, we are redefining the Corporate Gifting Landscape in India.
                    We promise transparent sourcing, timely delivery, and a selection that ranges
                    from the traditional to the trendsetting.
                  </span>
                </Text>
                <Button variant="primary" size="large" onClick={() => navigate('/contact')}>
                  Get in Touch
                </Button>
              </BlockStack>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
